import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  IHaccpCreate,
  IHaccpPerformRequest,
  IHaccpPostpone,
  IHaccpPerformResponse,
} from '../models/haccp.model';
import { getDepartmentId, getTenantId } from '../../services/store-snapshot.service';
import { IdName } from '../../shared/models/id-name';
import { HaccpLog } from '../models/haccp-log.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';

@Injectable({ providedIn: 'root' })
export class HaccpApiService {
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/haccp`;
  }

  private get settingsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/haccp`;
  }

  constructor(private http: HttpClient) {}

  loadHaccpVersionById(haccpId: string, haccpVersionId: string): Observable<IHaccpPerformResponse> {
    return this.http.get<IHaccpPerformResponse>(`${this.url}/${haccpId}/log/${haccpVersionId}`);
  }

  loadHaccpLog(
    haccpId: string,
    startDate?: Date,
    endDate?: Date,
    showAllColumns?: boolean,
    sortByPerformedAscending?: boolean
  ): Observable<HaccpLog> {
    let params = new HttpParams();
    if (startDate) {
      params = params.set('from', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('to', endDate.toISOString());
    }
    if (showAllColumns) {
      params = params.set('showAllColumns', showAllColumns.toString());
    }
    if (sortByPerformedAscending) {
      params = params.set('sortByPerformedAscending', sortByPerformedAscending.toString());
    }
    return this.http.get<HaccpLog>(`${this.url}/${haccpId}/log`, { params });
  }

  createHaccp(haccp: IHaccpCreate): Observable<IdName> {
    const formData = FormDataService.mapToFormData(haccp);
    return this.http.post<IdName>(this.url, formData);
  }

  updateHaccp(haccp: IHaccpCreate): Observable<void> {
    const formData = FormDataService.mapToFormData(haccp);
    return this.http.put<void>(`${this.url}/${haccp.id}`, formData);
  }

  deleteHaccp(haccpId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${haccpId}`);
  }

  performHaccp(haccp: IHaccpPerformRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(haccp);
    const url = `${this.url}/${haccp.id}/perform`;
    return this.http.put<void>(url, formData);
  }

  updateHaccpPerform(haccp: IHaccpPerformRequest, versionId: string): Observable<void> {
    const formData = FormDataService.mapToFormData(haccp);
    const url = `${this.url}/${haccp.id}/${versionId}/UpdateLog`;
    return this.http.put<void>(url, formData);
  }

  postponeHaccp({ id, newExpiryDate, comment }: IHaccpPostpone): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/postpone`, { newExpiryDate, comment });
  }

  restoreHaccp(id: string): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/restore`, {});
  }

  loadHaccpDraftPerform(haccpId: string): Observable<IHaccpPerformResponse> {
    return this.http.get<IHaccpPerformResponse>(`${this.url}/${haccpId}/draftPerform`);
  }

  saveHaccpDraftPerform(haccp: IHaccpPerformRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(haccp);
    return this.http.put<void>(`${this.url}/${haccp.id}/draftPerform`, formData);
  }

  loadHaccpPerform(haccpId: string, haccpVersionId: string): Observable<IHaccpPerformResponse> {
    return this.http.get<IHaccpPerformResponse>(`${this.url}/${haccpId}/log/${haccpVersionId}`);
  }

  printExcel(id: string, from?: Date, to?: Date, showAllColumns?: boolean): Observable<Blob> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    if (showAllColumns) {
      params = params.set('showAllColumns', showAllColumns.toString());
    }
    const url = `${this.url}/${id}/Excel`;
    return this.http.get(url, { params, responseType: 'blob' });
  }
}
